import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import ReactDOM from 'react-dom';
import dayjs from 'core/utils/dayjs';
import globalDyHandler from 'core/utils/helpers/dyHandler';

import MerchantShopNowLinkLabel from 'core/modules/MerchantShopNowLinkLabel/MerchantShopNowLinkLabel';
import PremierMerchantRebate from 'core/modules/PremierMerchantRebate/PremierMerchantRebate';

import { triggerStickyFooterDesktopIsToggledGAEvent, triggerStickyFooterDesktopIsViewed } from '../../actions';

import { selectStickyFooterAllDataIsLoaded } from '../../selectors';

import './StickyFooterDesktop.scss';

export function StickyFooterDesktop({ placement, config }) {
  const dispatch = useDispatch();
  const elementContainer = document.getElementsByClassName('mn_pageBody')[0];
  const allDataIsLoaded = useSelector(selectStickyFooterAllDataIsLoaded);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isSuspendedFromOutside, setIsSuspendedFromOutside] = useState(globalDyHandler.isDesktopStickyFooterSuspended);

  const shouldShowStickyFooter = !isEmpty(placement) && !isSuspendedFromOutside;

  useEffect(() => {
    if (shouldShowStickyFooter) {
      dispatch(triggerStickyFooterDesktopIsViewed(placement, config));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement]);

  useEffect(() => {
    setTimeout(() => setIsExpanded(shouldShowStickyFooter), 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement, shouldShowStickyFooter]);

  useEffect(() => {
    const suspendStickyFooterFromOutside = () => {
      setIsExpanded(false);
      setTimeout(() => { setIsSuspendedFromOutside(true); }, 1000);
    };

    const forceStickyFooterFromOutside = () => {
      setIsSuspendedFromOutside(false);
    };

    globalDyHandler.suspendStickyFooterFromOutside = suspendStickyFooterFromOutside;
    globalDyHandler.forceStickyFooterFromOutside = forceStickyFooterFromOutside;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleStickyFooter = () => {
    setIsExpanded(!isExpanded);
    dispatch(triggerStickyFooterDesktopIsToggledGAEvent(isExpanded, placement, config));
  };

  // API response doesn't return content
  if (allDataIsLoaded && isEmpty(placement)) {
    return ReactDOM.createPortal(
      <div className="mn_stickyFooterDesktopWrap mn_stickyFooterAdUnitWrap mn_stickyFooterNoContent" />,
      elementContainer,
    );
  }

  // API request is still in progress or Sticky Footer is suspended from outside the component
  if (!shouldShowStickyFooter) {
    return null;
  }

  const { stickyFooterDesktop: { rebateOptions }, api: { contentParams } } = config;
  const contentGroupIds = contentParams.map(({ content_group_id }) => content_group_id).join(',');

  const {
    merchant, isOngoing, stopDatetime, couponCode, disclaimer, clickUrl, id,
    assets: { image, rolloverCopy, isImage150By150 },
  } = placement;
  const endDate = dayjs.parseZone(stopDatetime).format('MMM. DD, YYYY');
  const { showRebate, rebate } = merchant;

  return ReactDOM.createPortal(
    <div className="mn_stickyFooterDesktopWrap mn_stickyFooterAdUnitWrap">
      <div className={`mn_stickyFooter mn_stickyFooterDesktop ${isExpanded ? 'mn_expanded' : 'mn_collapsed'}`}>
        <div className="mn_stickyFooterContentWrap">
          <button
            className="mn_stickyFooterControl"
            label={`${isExpanded ? 'Hide' : 'Show'} offer`}
            aria-expanded={isExpanded}
            onClick={toggleStickyFooter}
          />
          <a
            className="mn_stickyFooterContent mn_contentWrap"
            title="Opens in new window"
            target="_blank"
            href={clickUrl}
            data-content-group-ids={contentGroupIds}
            data-offer-id={id}
            data-merchant-id={merchant.id}
            data-merchant-name={merchant.name}
          >
            <div className={`mn_imageWrap ${isImage150By150 ? 'mn_imageWrap150By150' : '' }`}>
              <img className="mn_image" src={image} alt="" />
            </div>
            <div className="mn_merchantInfo">
              <div className="mn_merchantLogoWrap">
                <img src={merchant.logoUrls._88x31} alt={merchant.name} />
              </div>
              <div className="mn_offerInfoAndRebateWrap">
                <div className="mn_offerInfoWrap">
                  <p className="mn_rolloverCopyAndDate">
                    <span className="mn_rolloverCopy">{rolloverCopy}</span>
                    <span className="mn_expirationDate">
                      {' '}{ isOngoing ? 'Ongoing' : `Valid through ${endDate}` }
                      <span className="mn_expirationDot">.</span>
                    </span>
                  </p>
                  { couponCode &&
                    <p className="mn_useCode">Use code: <span className="mn_code">{couponCode}</span></p> }
                  { disclaimer && <p className="mn_disclaimer">{disclaimer}</p> }
                </div>
                <div className="mn_rebateWrap">
                  { showRebate && <PremierMerchantRebate {...rebate} {...rebateOptions} /> }
                </div>
              </div>
            </div>
            <div className="mn_buttonWrap">
              <button className="mn_button mn_shopNow">
                <MerchantShopNowLinkLabel merchant={merchant} />
              </button>
            </div>
          </a>
        </div>
      </div>
    </div>,
    elementContainer,
  );
}

export default StickyFooterDesktop;
